import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Topbar from "../components/Topbar";
import { useAuth } from "../context/AuthContext";
import { listHotels } from "../api/hotels";
import { deleteCompetitor, listCompetitors } from "../api/competitors";
import { HotelOut } from "../types/hotels";
import { CompetitorOut } from "../types/competitors";
import { formatGuests } from "../utils/guests";
import {
  getHotelDisplaySettings,
  loadHotelsDisplaySettings,
  type HotelDisplaySettings,
} from "../utils/hotelDisplaySettings";
import {
  ArrowLeft,
  ExternalLink,
  Building2,
  MapPin,
  BedDouble,
  MoonStar,
  Sparkles,
  Plus,
  CircleCheck,
  Clock,
  Trash2,
  Users,
} from "lucide-react";
import AddCompetitorOtaModal from "../components/modals/AddCompetitorOtaModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";

export default function CompanyHotelDetailsPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const hotelId = useMemo(() => Number(id), [id]);
  const { company_id: companyId } = useAuth();

  const [hotel, setHotel] = useState<HotelOut | null>(null);
  const [displaySettingsMap, setDisplaySettingsMap] = useState<Record<string, HotelDisplaySettings>>({});
  const [loadingHotel, setLoadingHotel] = useState(false);
  const [errorHotel, setErrorHotel] = useState("");

  const [competitors, setCompetitors] = useState<CompetitorOut[]>([]);
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [errorCompetitors, setErrorCompetitors] = useState("");

  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [deletingCompetitor, setDeletingCompetitor] = useState<CompetitorOut | null>(null);

  useEffect(() => {
    if (!companyId || !Number.isFinite(hotelId)) return;
    setLoadingHotel(true);
    setErrorHotel("");
    (async () => {
      try {
        const data = await listHotels(companyId, { page: 1, per_page: 200 });
        const nextDisplaySettingsMap = await loadHotelsDisplaySettings(data.items);
        const found = data.items.find((h) => h.id === hotelId) || null;
        setDisplaySettingsMap(nextDisplaySettingsMap);
        setHotel(found);
      } catch (e: any) {
        setErrorHotel(e?.response?.data?.detail || "Ошибка загрузки объекта");
      } finally {
        setLoadingHotel(false);
      }
    })();
  }, [companyId, hotelId]);

  const fetchCompetitors = useCallback(async () => {
    if (!companyId || !Number.isFinite(hotelId)) return;
    setLoadingCompetitors(true);
    setErrorCompetitors("");
    try {
      const data = await listCompetitors({
        company_id: companyId,
        company_hotel_id: hotelId,
        page: 1,
        per_page: 100,
      });
      const items = Array.isArray(data.items) ? data.items : [];
      const sorted = [...items].sort((a, b) => Number(b.approved) - Number(a.approved));
      setCompetitors(sorted);
    } catch (e: any) {
      setErrorCompetitors(e?.response?.data?.detail || "Ошибка загрузки конкурентов");
    } finally {
      setLoadingCompetitors(false);
    }
  }, [companyId, hotelId]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deletingCompetitor) return;
    await deleteCompetitor(deletingCompetitor.id);
    setDeletingCompetitor(null);
    await fetchCompetitors();
  }, [deletingCompetitor, fetchCompetitors]);

  useEffect(() => {
    fetchCompetitors();
  }, [fetchCompetitors]);

  const approvedCount = useMemo(() => competitors.filter(c => c.approved).length, [competitors]);
  const pendingCount = Math.max(competitors.length - approvedCount, 0);
  const otaLimit = hotel?.ota_count ?? 3;
  const displaySettings = getHotelDisplaySettings(displaySettingsMap, hotel);
  const minStayLabel = displaySettings.min_stay?.length ? displaySettings.min_stay.join(", ") : "Не задан";
  const stats = [
    { label: "Всего конкурентов", value: competitors.length ? competitors.length : "—", desc: "в базе объекта" },
    { label: "Подтверждено", value: approvedCount, desc: "Ведется парсинг" },
    { label: "Ожидают проверки", value: pendingCount, desc: "Проходят проверку" },
    { label: "Максимум ОТА", value: otaLimit, desc: "ссылок на каждого конкурента" },
    { label: "Мин. stay", value: minStayLabel, desc: "для вывода данных", icon: MoonStar },
    { label: "Количество гостей", value: formatGuests(displaySettings.guests, displaySettings.guests_child), desc: "для вывода данных", icon: Users },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 text-slate-900">
      <Topbar />

      <main className="mx-auto w-full max-w-[1400px] px-6 py-10">
        <section className="mb-10 space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="space-y-4">
              <button
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-5 py-2 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
                onClick={() => navigate(-1)}
              >
                <ArrowLeft size={18} /> Назад
              </button>
              <div className="flex items-center gap-4">
                <div className="inline-grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-indigo-50 text-indigo-600 shadow-inner">
                  <Building2 size={26} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.35em] text-slate-400">{hotel?.city || "—"}</p>
                  <h1 className="mt-1 text-3xl font-bold text-slate-900">
                    {loadingHotel ? "Загрузка…" : hotel?.name || "Объект не найден"}
                  </h1>
                  <p className="mt-2 text-sm text-slate-500">
                    {hotel ? `${hotel.type} · ${hotel.city}` : "Проверьте, существует ли объект"}
                  </p>
                  {hotel?.ota_links?.length ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {hotel.ota_links.map((link) => (
                        <a
                          key={`${link.label}-${link.url}`}
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
                        >
                          {link.label}
                          <ExternalLink size={12} />
                        </a>
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {(hotel?.ai_reports_enabled ?? Boolean(hotel?.ai_report_days?.length || hotel?.ai_report_settings?.days?.length)) && <button
                type="button"
                disabled={!hotel?.group_id}
                title={hotel?.group_id ? "Открыть ИИ-отчёт" : "Для объекта не задана группа парсинга"}
                onClick={() => navigate(`/ai-report?group_id=${hotel?.group_id}`)}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-indigo-200 bg-indigo-50 px-5 py-2.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles size={18} /> ИИ-отчёт
              </button>}
              {hotel?.url && (
                <a
                  href={hotel.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
                >
                  <ExternalLink size={18} />
                  Открыть ссылку
                </a>
              )}
              <button
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105"
                onClick={() => setOpenAdd(true)}
              >
                <Plus size={18} />
                Добавить конкурента
              </button>
            </div>
          </div>

          {errorHotel && (
            <div className="rounded-3xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-amber-800 shadow-inner">
              {errorHotel}
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {stats.map((stat, idx) => {
              const Icon = stat.icon;
              return (
              <div
                key={idx}
                className="rounded-3xl border border-white/60 bg-white/90 p-5 shadow-[0_14px_36px_rgba(15,23,42,0.08)] backdrop-blur"
              >
                <p className="text-xs uppercase tracking-wide text-slate-400">{stat.label}</p>
                <div className="mt-2 flex items-center gap-2 text-2xl font-semibold text-slate-900">
                  {Icon ? <Icon size={20} className="shrink-0 text-indigo-500" /> : null}
                  <span>{stat.value}</span>
                </div>
                <p className="mt-1 text-sm text-slate-500">{stat.desc}</p>
              </div>
              );
            })}
          </div>
        </section>

        <section className="space-y-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Сравнение</p>
              <h2 className="text-2xl font-semibold text-slate-900">Конкуренты объекта</h2>
              <p className="text-sm text-slate-500">
                {competitors.length
                  ? "Следите за статусами и характеристиками конкурентов"
                  : "Добавьте конкурентов, чтобы начать сравнение"}
              </p>
            </div>
            <div className="inline-flex items-center gap-2 rounded-full border border-indigo-100 bg-indigo-50 px-4 py-2 text-xs font-semibold text-indigo-700">
              Максимум ОТА: {otaLimit}
            </div>
          </div>

          {errorCompetitors && (
            <div className="rounded-3xl border border-amber-200 bg-amber-50/90 px-5 py-4 text-sm text-amber-800 shadow-inner">
              {errorCompetitors}
            </div>
          )}

          {loadingCompetitors && (
            <div className="rounded-3xl border border-dashed border-slate-200 bg-white/70 p-10 text-center text-slate-500 animate-pulse">
              Загружаем список конкурентов…
            </div>
          )}

          {!loadingCompetitors && !errorCompetitors && competitors.length === 0 && (
            <div className="rounded-3xl border border-slate-200 bg-white/80 p-10 text-center text-slate-500">
              Конкурентов пока нет
              <div className="mt-4 flex justify-center">
                <button
                  onClick={() => setOpenAdd(true)}
                  className="inline-flex items-center gap-2 rounded-2xl border border-indigo-200 bg-white px-5 py-2 text-sm font-semibold text-indigo-600 shadow-sm transition hover:bg-indigo-50"
                >
                  <Plus size={16} />
                  Добавить первого конкурента
                </button>
              </div>
            </div>
          )}

          {!loadingCompetitors && competitors.length > 0 && (
            <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {competitors.map(c => (
                <CompetitorCard
                  key={c.id}
                  item={c}
                  onDelete={() => {
                    setDeletingCompetitor(c);
                    setOpenDelete(true);
                  }}
                />
              ))}
            </div>
          )}
        </section>

        <AddCompetitorOtaModal
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          companyId={companyId}
          hotelId={Number.isFinite(hotelId) ? hotelId : null}
          otaLimit={otaLimit}
          onSubmitted={fetchCompetitors}
        />

        <ConfirmDeleteModal
          open={openDelete}
          onClose={() => {
            setOpenDelete(false);
            setDeletingCompetitor(null);
          }}
          onConfirm={handleDeleteConfirm}
          title="Удаление конкурента"
          message={
            deletingCompetitor
              ? `Вы точно хотите удалить конкурента — ${deletingCompetitor.url || deletingCompetitor.name || "—"}?`
              : "Вы уверены, что хотите удалить конкурента?"
          }
        />
      </main>
    </div>
  );
}

function CompetitorCard({
  item,
  onDelete,
}: {
  item: CompetitorOut;
  onDelete: () => void;
}) {
  return (
    <div className="group flex h-full flex-col rounded-3xl border border-white/70 bg-white/90 shadow-[0_18px_38px_rgba(15,23,42,0.12)] transition-transform duration-200 hover:-translate-y-1 hover:shadow-[0_25px_60px_rgba(99,102,241,0.25)]">
      {/*
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded-t-3xl bg-slate-100">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={item.name || "Конкурент"}
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.03]"
            loading="lazy"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-400">
            <Building2 size={56} />
          </div>
        )}
        {item.platform && (
          <div className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-slate-700 shadow">
            {item.platform}
          </div>
        )}
        {typeof item.stars === "number" && (
          <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded-full bg-amber-100/90 px-3 py-1 text-xs font-semibold text-amber-700 shadow">
            <Star size={14} className="fill-amber-400 text-amber-500" />
            {item.stars}
          </div>
        )}
      </div>
      */}

      <div className="flex flex-1 flex-col gap-4 p-5">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-lg font-semibold text-slate-900">{item.name || "—"}</div>
              {item.ota_links?.length ? (
                <div className="mt-2 flex flex-wrap gap-2">
                  {item.ota_links.map((link) => (
                    <a
                      key={`${link.label}-${link.url}`}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded-full border border-indigo-100 bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700 transition hover:bg-indigo-100"
                    >
                      {link.label}
                      <ExternalLink size={12} />
                    </a>
                  ))}
                </div>
              ) : null}
              {item.address && (
                <div className="mt-1 flex items-center gap-2 text-sm text-slate-500">
                  <MapPin size={16} className="text-slate-400" />
                  <span className="truncate">{item.address}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${
                  item.approved ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-700"
                }`}
                title={item.approved ? "Подтверждено" : "Ожидает подтверждения"}
              >
                {item.approved ? <CircleCheck size={14} /> : <Clock size={14} />}
                <span>{item.approved ? "Подтверждено" : "Ожидает"}</span>
              </span>
              <button
                type="button"
                onClick={onDelete}
                className="inline-flex items-center justify-center rounded-full border border-rose-200 bg-rose-50 p-2 text-rose-600 transition hover:bg-rose-100"
                title="Удалить конкурента"
              >
                <Trash2 size={16} />
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 text-xs font-medium text-slate-600">
            {typeof item.review_rating === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-3 py-1 text-indigo-700">
                <Sparkles size={14} />
                <span>Рейтинг {item.review_rating}</span>
                {typeof item.review_count === "number" && item.review_count > 0 && (
                  <span className="text-indigo-400">({item.review_count})</span>
                )}
              </span>
            )}
            {typeof item.room_count === "number" && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">
                <BedDouble size={14} className="text-slate-500" />
                <span>Номеров: {item.room_count}</span>
              </span>
            )}
            {item.added_by && (
              <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1">
                <Sparkles size={12} className="text-purple-500" />
                <span>Добавил: {item.added_by}</span>
              </span>
            )}
          </div>
        </div>

        {item.url && (
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            Открыть объект
            <ExternalLink size={16} />
          </a>
        )}

        {/*
        <div className="relative mt-auto space-y-2 pb-6 text-sm text-slate-600">
          <button
            className="inline-flex w-full items-center justify-between rounded-2xl bg-slate-100/80 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
            onClick={() => setAmenitiesOpen(v => !v)}
            type="button"
          >
            Удобства
            <ChevronDown
              size={16}
              className={`transition-transform ${amenitiesOpen ? "rotate-180" : ""}`}
            />
          </button>

          <div
            className={`absolute left-0 right-0 top-full z-10 mt-2 rounded-2xl border border-slate-100 bg-white/95 px-3 py-3 shadow-lg transition-all duration-200 ${
              amenitiesOpen ? "pointer-events-auto opacity-100 translate-y-0" : "pointer-events-none opacity-0 -translate-y-2"
            }`}
          >
            {amenities.length === 0 ? (
              <div className="text-sm text-slate-500">Данные об удобствах недоступны</div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {amenities.map(a => (
                  <span
                    key={a}
                    className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
                  >
                    {a}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        */}
      </div>
    </div>
  );
}
